import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { intersection, isNaN } from 'lodash';

import {
    actionTypes,
    actions, setDefaultPlaceholderImageSuccess, setDefaultPlaceholderImageFailure
} from '../actions/appActions';
import {
    setFilterOptions, setFilterValue, resetAllFilters, setPriceRange
} from '../actions/filtersActions';
import { selectors as userSelectors } from '../reducers/userReducer';
import { setProductClassOptions, setProductClassValue } from '../actions/productClassesActions';
import { setTree, initProductTreeRoots } from '../actions/sectorsActions';
import { fetchCollections } from '../actions/collectionsActions';
import { removeSplashScreenLoader } from '../helpers';
import {
    fetchBrandFilter,
    fetchClassificationTree,
    fetchEtimClassFilter,
    // getProductInfoMap,
    getAllProductProperties,
    // fetchAnagrafica,
    fetchProductTreeRootsFilter,
    getUserGroups,
    fetchPriceRangeFilter,
    fetchPreferences, getAssetURL
} from '../api';
import * as constants from '../constants';
import { selectors } from '../reducers/appReducer';
import { getApiPayload } from '../selectors/filterPayload';
import { setLocalStorageFilters, setLocalStorageFiltersSuccess } from '../actions/localstorageActions';
import { getStoredFilters } from '../utils/LocalStorageFilterUtils';
import { resetProductClassSaga } from './productClassesSaga';
import { setIsChangingClassification } from '../actions/pageStateActions';
import { loadCustomFiltersOptions } from './filtersSaga/customFiltersSaga';
import { buildApiLocales } from '../intl-helpers';
import { checkImageExists } from '../lib/api/assets';

export function* bootstrapApp() {
    try {

        // *** CARICAMENTO DAL LOCAL STORAGE *** //
        const canLoadFiltersFromLocalStorage = yield select(selectors.getLoadFiltersFromLocalStorage)
        // *** - *** //

        const user = yield select(userSelectors.getUser);

        yield put(actions.bootstrapAppStart());

        // As first operation, call initializeBootstrappedState to get and set the default state value
        // console.warn('CALLED BOOTSTRAP APP!')
        yield call(initializeBootstrappedState)

        // *** CARICAMENTO DAL LOCAL STORAGE *** //
        // Se il localStorage è attivo, carichiamo i filtri salvati e rifacciamo le chiamate API
        // con il payload aggiornato (potrebbe includere filtri utente).
        // Se è disattivo, lo state è già corretto dal primo bootstrap (initializeBootstrappedState)
        // quindi evitiamo le doppie chiamate API.
        if(canLoadFiltersFromLocalStorage) {
            // Get filter from state
            const apiStoredFilters = user?.preferences ? user.toJS().preferences.user_filters ? user.toJS().preferences.user_filters : {} : {};
            //console.warn('apiStoredFilters', apiStoredFilters)

            // Get filters from localStorage and put into the related state
            const storedFilters = Object.keys(apiStoredFilters).length !== 0 ? apiStoredFilters : getStoredFilters()
            //console.warn('storedFilters', storedFilters)

            if (storedFilters) {
                // Dispatch an action to update Redux state with the localStorage stored filters
                yield put(setLocalStorageFilters({ userFilters: storedFilters }))
            }

            const productTreeRootsRes = yield call(fetchProductTreeRootsFilter);

            yield put(initProductTreeRoots(productTreeRootsRes.data));

            // Get apiPayload from Redux state (potrebbe contenere filtri dal localStorage)
            const apiPayload = yield select(getApiPayload)

            const calls = [
                call(fetchBrandFilter, apiPayload),
                call(fetchClassificationTree, apiPayload),
                call(fetchEtimClassFilter, apiPayload),
                call(fetchPriceRangeFilter, apiPayload),
                // call(getProductInfoMap),
                call(getAllProductProperties),
                // call(fetchAnagrafica),
            ];

            if (user.ruolo === 'admin') {
                calls.push(call(getUserGroups));
            }

            const [
                brandsRes,
                treeRes,
                etimClassRes,
                priceRangeRes,
                // productInfoMapRes,
                productPropertiesRes,
                // anagraficaRes,
                userGroupsRes,
            ] = yield all(calls);


            // console.log("brandRes from appSaga => ", brandsRes)
            yield put(setFilterOptions(constants.BRAND_FILTER, brandsRes.data))

            //yield put(setPriceRange(priceRangeRes.data));
            // yield put(actions.setProductInfoMap(productInfoMapRes.data));

            yield put(actions.setProductProperties(productPropertiesRes.data))

            yield put(setProductClassOptions(etimClassRes.data))
            yield put(setTree(treeRes.data))

            if (user.ruolo === 'admin') {
                yield put(actions.setUserGroups(userGroupsRes.data))
            } else {
                // Se non sono admin verifico che le colonne selezionate per la tabella personalizzata
                // siano tra quelle per cui il mio gruppo ha il permesso di visualizzazione
                const currentTableColumns = yield select(selectors.getTableColumns)

                if (currentTableColumns && currentTableColumns.length > 0) {
                    const validProps = productPropertiesRes.data.map((p) => p.code)

                    yield put(actions.setTableColumns(intersection(currentTableColumns, validProps)))
                }
            }

            yield put(fetchCollections())
        }
        // *** - *** //

        const imagePlaceholder = getAssetURL(`no-image.png`)

        yield put(actions.checkDefaultPlaceholderImage(imagePlaceholder))

        yield put(actions.bootstrapAppSuccess())
    } catch (err) {
        // TODO: gestire messaggio di errore
        yield put(actions.bootstrapAppFail('Unable to contact remote server'))
        console.error(err)
    }
}

function* handleLoadPreferences() {
    try {
        const preferencesRes = yield call(fetchPreferences)

        if (preferencesRes.data) {
            const appName = preferencesRes.data.filter(
                (preference) => preference.name === 'appName'
            )

            if (appName.length > 0 && appName[0].value !== '') {
                yield put(actions.setAppName(appName[0].value));
            }

            const appColor = preferencesRes.data.filter(
                (preference) => preference.name === 'appColor'
            )

            if (appColor.length > 0) {
                window.document.body.style.setProperty('--color-primary', appColor[0].value)
            }

            const alternateRowColor = preferencesRes.data.filter(
                (preference) => preference.name === 'alternateRowColor'
            )

            if (alternateRowColor.length > 0) {
                window.document.body.style.setProperty(
                    '--color-alternate-row',
                    alternateRowColor[0].value
                )
            }

            const priceDecimalPrecision = preferencesRes.data.filter(
                (preference) => preference.name === 'priceDecimalPrecision'
            )

            if (priceDecimalPrecision.length > 0 && !isNaN(parseInt(priceDecimalPrecision[0].value))) {
                yield put(
                    actions.setAppParameter(
                        'priceDecimalPrecision',
                        parseInt(priceDecimalPrecision[0].value)
                    )
                )
            }
        }
    } catch (err) {
        console.error(err)
    }
}

function hideLoadingSplashScreen() {
    removeSplashScreenLoader()
}

function* initializeBootstrappedState() {
    const user = yield select(userSelectors.getUser)
    const userLocale = user.locale || 'it_IT'

    yield call(resetProductClassSaga)
    // for now, I use setIsChangingClassification to prevent fetchItems to be executed
    yield put(setIsChangingClassification(true))
    yield put(setProductClassValue(null))

    // Get apiPayload from Redux state
    // const apiPayload = yield select(getApiPayload)
    const defaultApiPayload = {
        "per_page": 40,
        "page": 1,
        "include_accessori": true,
        "include_ricambi": true,
        "locales": buildApiLocales(userLocale),
        "product_tree": "idrolab"
    }
    // console.warn('apiPayload => ', apiPayload)
    // console.warn('apiPayloadDefault => ', defaultApiPayload)

    const calls = [
        call(fetchBrandFilter, defaultApiPayload),
        call(fetchClassificationTree, defaultApiPayload),
        call(fetchPriceRangeFilter, defaultApiPayload),
        // call(getProductInfoMap),
        call(getAllProductProperties),
        // call(fetchAnagrafica),
    ]

    yield call(loadCustomFiltersOptions, defaultApiPayload)

    if (user.ruolo === 'admin') {
        calls.push(call(getUserGroups));
    }

    const [
        brandsRes,
        treeRes,
        priceRangeRes,
        productPropertiesRes,
        userGroupsRes,
    ] = yield all(calls);

    yield put(setFilterOptions(constants.BRAND_FILTER, brandsRes.data))

    yield put(setPriceRange(priceRangeRes.data))

    yield put(actions.setProductProperties(productPropertiesRes.data))

    yield put(setTree(treeRes.data))

    if (user.ruolo === 'admin') {
        yield put(actions.setUserGroups(userGroupsRes.data))
    } else {
        // Se non sono admin verifico che le colonne selezionate per la tabella personalizzata
        // siano tra quelle per cui il mio gruppo ha il permesso di visualizzazione
        const currentTableColumns = yield select(selectors.getTableColumns)

        if (currentTableColumns && currentTableColumns.length > 0) {
            const validProps = productPropertiesRes.data.map((p) => p.code)

            yield put(actions.setTableColumns(intersection(currentTableColumns, validProps)))
        }
    }

    yield put(fetchCollections())

    const productTreeRootsRes = yield call(fetchProductTreeRootsFilter)

    yield put(initProductTreeRoots(productTreeRootsRes.data));

    // Salvo i valori correnti dei filtri (potrebbero essere stati settati dalla pagina pubblica)
    const currentFilters = yield select((state) => state.filters)
    const savedFullText = currentFilters.get(constants.FULL_TEXT_FILTER).get('value')
    const savedBrands = currentFilters.get(constants.BRAND_FILTER).get('value')

    // Resetto i filtri per catturare uno stato bootstrapped PULITO
    yield put(resetAllFilters())

    // Create the bootstrappedState object and send to reducer
    const filters = yield select((state) => state.filters)
    const productClasses = yield select((state) => state.productClasses)
    const sectors = yield select((state) => state.sectors)

    const bootstrappedState = {
        filters,
        productClasses,
        sectors,
    }

    yield put(actions.setBootstrappedAppState(bootstrappedState))
    // the bootstrappedState is setted, we can setIsChangingClassification to false so fetchItems can run
    yield put(setIsChangingClassification(false))

    // Ri-setto i filtri salvati DOPO il bootstrap, così sono visibili all'utente
    // ma NON fanno parte dello snapshot (e possono essere rimossi normalmente)
    if (savedFullText && savedFullText !== '') {
        yield put(setFilterValue(constants.FULL_TEXT_FILTER, savedFullText))
    }
    if (savedBrands && savedBrands.size > 0) {
        for (const brand of savedBrands.toJS()) {
            yield put(setFilterValue(constants.BRAND_FILTER, brand))
        }
    }
}

function* checkImageExistsSaga(action) {
    try {
        const { url } = action.payload;

        // Controllo se l'immagine esiste
        const imageExists = yield call(checkImageExists, url);

        if (imageExists) {
            // Se l' immagine esiste, per bypassare il problema della cache aggressiva di chrome gli passo un timestamp come query parameter.
            yield put(setDefaultPlaceholderImageSuccess(`${url}?t=${Date.now()}`));
        } else {
            yield put(setDefaultPlaceholderImageSuccess('./images/no-photo.png'));
        }
    } catch (error) {
        yield put(setDefaultPlaceholderImageFailure(error.message));
    }
}

export default [
    takeEvery(actionTypes.BOOTSTRAP_APP, bootstrapApp),
    takeEvery(actionTypes.HIDE_LOADING_SPLASH_SCREEN, hideLoadingSplashScreen),
    takeEvery(actionTypes.LOAD_PREFERENCES, handleLoadPreferences),
    takeLatest(actionTypes.INITIALIZE_BOOTSTRAPPED_STATE, initializeBootstrappedState),
    takeEvery(actionTypes.CHECK_DEFAULT_PLACEHOLDER_IMAGE, checkImageExistsSaga)
];
